import {applyMiddleware, combineReducers, compose, createStore, Reducer, Store, StoreEnhancer} from "redux";
import promiseMiddleware from "redux-promise";
import {persistReducer, persistStore} from "redux-persist";
import storage from "redux-persist/lib/storage";
import defaultStore, {IStore} from "./defaultStore";
import metaReducer from "./meta/metaReducer";
import authReducer from "./auth/authReducer";
import affiliateReducer from "./affiliateReferrals/affiliateReducer";
import rttrReducer from "./rttr/rttrReducer";
import acquisitionReducer from "./acquisition/acquisitionReducer";
import {getSentryReduxEnhancer} from "../utils/sentry";

const metaPersistConfig = {
    key: "meta",
    storage,
    whitelist: [
        "menuOrders",
        "dismissedWalletConnectPrompt",
        "lastSearchedPlaceId",
        "lastMenuOrderId",
        "lastMerchantId",
        "magicLoginExternalReferralId",
        "referralCode",
        "accountLevel",
        "themePreference",
        "lastDeletionEmailSent",
        "checkedMagicActive",
        "fuelPriceExpiryTime",
        "isIndustryDismissed",
        "isGoFrensTutorialBannerDismissed",
        "lastActivePaymentMethod",
        "isCheckoutFuelBannerDismissed",
        "isFuelUpModalDismissed",
        "isOverwolfDiscordBannerDismissed",
        "isDevourplayFeatureModalDismissed",
        "streamerModeEnabled",
        "dismissedBrandChallengeOnboardings",
    ],
};

const authPersistConfig = {
    key: "auth",
    storage,
    whitelist: ["fullToken"],
};

const affiliatePersistConfig = {
    key: "affiliate",
    storage,
    whitelist: [
        "referralCode",
        "referralExpiry",
    ],
};

/*
 * Intentionally empty whitelist: realmUuid is re-supplied by Realm on every
 * webframe load, so persisting it would only create cross-account leakage on
 * shared devices without saving any round trips. The slice still lives in
 * Redux for in-memory access during a session.
 */
const rttrPersistConfig = {
    key: "rttr",
    storage,
    whitelist: [] as Array<string>,
};

/*
 * Persisted because the whole point is surviving the email-code round trip:
 * the user lands tagged, leaves for their inbox, and comes back on a fresh
 * page load with a bare URL. In memory only, the signal is gone by then.
 */
const acquisitionPersistConfig = {
    key: "acquisition",
    storage,
    whitelist: [
        "utmSource",
        "utmMedium",
        "utmCampaign",
        "referrer",
        "landingPage",
        "acquisitionExpiry",
    ],
};

const reducers: Reducer<any, any> = combineReducers({
    metaStore: persistReducer(metaPersistConfig, metaReducer),
    authStore: persistReducer(authPersistConfig, authReducer),
    affiliateStore: persistReducer(affiliatePersistConfig, affiliateReducer),
    rttrStore: persistReducer(rttrPersistConfig, rttrReducer),
    acquisitionStore: persistReducer(acquisitionPersistConfig, acquisitionReducer),
});

const middleware: StoreEnhancer = compose(
    applyMiddleware(promiseMiddleware),
    getSentryReduxEnhancer(),
);

export const store: Store<IStore> = createStore(reducers, defaultStore, middleware);
export const persistor = persistStore(store);
